/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
import {
  createLineItemNoteApi,
  getPatientsApi,
  listFailedAdjudicationsApi,
  listLineItemNotesApi,
  noteActionApi,
  updateRefillAdjudicationStateApi,
} from 'api/patient';
import {
  closeEScriptRequestsApi,
  importEScriptRequestsApi,
  listBulkLabelPrintHistoryApi,
  listBulkLabelsApi,
  listEVisitRequestsApi,
  listLineItemsApi,
  listOrdersForProductFulfillmentApi,
  listRxIntakeRequestsApi,
} from 'api/requests';
import { debounce, find, first, flatMap, isEqual, map, omit, some } from 'lodash';
import { patientActions } from 'store/slices/patientSlice';
import { shipmentModuleActions } from 'store/slices/shipmentModuleSlice';
import { workQueueActions } from 'store/slices/workQueueSlice';

import { listOrderLineItemsApi } from 'api/orderLineItems';
import { listRelayOrdersApi } from 'api/relay';
import {
  applyUrgentTagApi,
  associateOrderShipmentApi,
  cancelBulkLineItemsApi,
  clearShipByDateOverrideApi,
  deleteIrreconciledOrderApi,
  deletePrxShipmentApi,
  getOrderShipmentsApi,
  getOrderSuggestionsForShipstationOrdersApi,
  getUnReconciledShipmentsListApi,
  listOrdersForShipmentApi,
  listProcessedExternalOrdersApi,
  removeUrgentTagApi,
  setLineItemShipByDateApi,
  updateShipmentApi,
} from 'api/order';
import { ContentType, NoteState } from 'common/constants/notes';
import { PatientStatus } from 'enums/patient.enum';
import WorkQueues, { DIRECTION_ALREADY_MAPPED } from 'enums/workQueue.enum';

import {
  associateCatalogProductApi,
  getPrescriptionReviewDataApi,
  listPrescriptionsForReviewApi,
  multiRxLineItemApi,
  overridePrescriptionAPI,
  resolveDrugProfileApi,
  resolveDuplicatePrescriptionApi,
  resolveShipLaterReviewApi,
  syncSigApi,
} from 'api/prescription';
import { applyFulfillingPharmacyFilter } from 'api/utils';
import { transferOrderToFacilityApi } from 'api/pharmacy';
import {
  listCentralFillOrdersApi,
  repostCentralFillOrderApi,
  resolveCentralFillReviewApi,
  updateCentralFillOrderStatusApi,
} from 'api/centralFill';
import { listLockedInventoryItemsApi } from 'api/inventory';
import {
  cancelFillLaterItemApi,
  createFillLaterItemNowApi,
  listFillLaterItemsApi,
  mergeFillLaterItemApi,
  rescheduleFillLaterItemApi,
  updateFillLaterAddressApi,
} from 'api/fillLater';
import {
  bringBackNonRoutableRequestToPomApi,
  cancelNonRoutableRequestApi,
  cancelRelayFaxApi,
  getNonRoutableRelayFaxDetailApi,
  getNonRoutableRequestApi,
  listNonRoutableRequestsApi,
  moveRelayFaxOrderToPomApi,
  reconcileRelayFaxApi,
  resendRelayFaxApi,
  retryNonRoutableRequestRoutingApi,
} from 'api/nonRoutableRequest';
import { EnableDispenseFlow, EnableInventoryLookup } from 'common/utils';
import { withTitleCasedDrugFields } from 'common/utils/titleCase';
import { handleError } from './errorHandlerThunks';

export const getOnboardedPatientsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return getPatientsApi({ page, sortBy, filter })
      .then(({ count, patients }) => {
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: patients, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedOnboardedPatients = debounce(({ dispatch }) => {
  return dispatch(getOnboardedPatientsByPage({ page: 1 }));
}, 1000);

export const sortOnboardedPatients =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedOnboardedPatients({ preset, dispatch });
  };

export const filterOnboardedPatients =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateFilter({ preset, filter }));

    // debounced to avoid making multiple api calls when removing individual filter too quickly
    debouncedOnboardedPatients({ preset, dispatch });
  };

export const getInClaimEligibilityRequestsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listLineItemsApi({ page, sortBy, filter })
      .then(({ count, lineItems }) => {
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: lineItems, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedInClaimEligibilityRequests = debounce(({ dispatch }) => {
  return dispatch(getInClaimEligibilityRequestsByPage({ page: 1 }));
}, 1000);

export const sortInClaimEligibilityRequestsWorkQueue =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedInClaimEligibilityRequests({ preset, dispatch });
  };

export const filterInClaimElgibilityRequests =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateFilter({ preset, filter }));

    // debounced to avoid making multiple api calls when removing individual filter too quickly
    debouncedInClaimEligibilityRequests({ preset, dispatch });
  };

export const getReviewInClaimEligibilityLineItemsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listLineItemsApi({ sortBy, filter, page })
      .then(({ count, lineItems }) => {
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: lineItems, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedReviewInClaimEligibilityLineItems = debounce(({ dispatch }) => {
  return dispatch(getReviewInClaimEligibilityLineItemsByPage({ page: 1 }));
}, 1000);

export const sortReviewInClaimEligibilityLineItemsWorkQueue =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedReviewInClaimEligibilityLineItems({ preset, dispatch });
  };

export const filterReviewInClaimEligibilityLineItems =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateFilter({ preset, filter }));

    // debounced to avoid making mulitple api calls when removing individual filter too quickly
    debouncedReviewInClaimEligibilityLineItems({ preset, dispatch });
  };

export const listOrderLineItemsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];
    const updatedFilter = applyFulfillingPharmacyFilter(getState, filter);
    const { selectedDrugProducts, ...apiFilter } = updatedFilter;

    // POM-6124: the Drug Name filter resolves to catalog productIds once the inventory lookup is
    // on, and to the free-text itemName match before that. Core ignores whichever key is not
    // live, but only ever send the one this build selects for rather than relying on that guard.
    const productIds =
      EnableInventoryLookup && selectedDrugProducts?.length
        ? flatMap(selectedDrugProducts, 'productIds')
        : undefined;

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listOrderLineItemsApi({ filter: { ...apiFilter, productIds }, page, sortBy })
      .then(({ count, orderLineItems }) => {
        dispatch(
          workQueueActions.loadWorkQueueToPage({ preset, page, items: orderLineItems, count })
        );
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedOrderLineItems = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listOrderLineItemsByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const sortOrderLineItemsWorkQueue =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedOrderLineItems({ preset, dispatch, getState });
  };

export const filterOrderLineItems =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedOrderLineItems({ preset, dispatch, getState });
  };

/**
 * POM-6061: whether a list response still describes the query the screen is asking for.
 *
 * The list calls are not cancellable and nothing serialises them, so two in-flight requests
 * settle in whatever order the network returns them. Sorting twice about a second apart, or
 * paging while a sort is still in flight, could therefore leave the OLDER response as the last
 * writer: the grid repainted with rows from the previous query while sortBy/filter/currentPage in
 * the store described the newer one. The selection is only cleared when those three CHANGE, so it
 * survived into a row set that no longer contained it, and the Tag Items / Print Labels badges
 * went on counting rows the operator could no longer see.
 *
 * Compared by value against the store rather than through a request counter: an identical repeat
 * request (a refresh) then still applies, and the check keeps no state of its own.
 */
const isStaleListResponse = ({ getState, preset, page, sortBy, filter }) => {
  const queue = getState().workQueue[preset];

  return (
    !queue ||
    queue.currentPage !== page ||
    !isEqual(queue.sortBy, sortBy) ||
    !isEqual(queue.filter, filter)
  );
};

export const listBulkLabelsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];
    const updatedFilter = applyFulfillingPharmacyFilter(getState, filter);
    const { selectedDrugProfiles, selectedDrugProducts, ...apiFilter } = updatedFilter;

    // POM-5850: the Drug Name filter resolves to catalog productIds once the inventory lookup is
    // on, and to drug_profile_ids before that. Core gates on INVENTORY_DISPENSE_FLOW and ignores
    // whichever key is not live, so only ever send the one this build selects for — sending both
    // would rely on core's guard rather than on the caller being correct.
    const drugProfileIds =
      !EnableInventoryLookup && selectedDrugProfiles?.length
        ? selectedDrugProfiles.map((drug) => drug.drugProfileId)
        : undefined;

    const productIds =
      EnableInventoryLookup && selectedDrugProducts?.length
        ? flatMap(selectedDrugProducts, 'productIds')
        : undefined;

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listBulkLabelsApi({
      filter: { ...apiFilter, drugProfileIds, productIds },
      page,
      sortBy,
    })
      .then(({ count, bulkLabels }) => {
        if (isStaleListResponse({ getState, preset, page, sortBy, filter })) return;

        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: bulkLabels, count }));
      })
      .catch((error) => {
        // The error is surfaced either way — an in-flight request that failed is still a failure
        // worth telling the user about — but only the newest request may empty the grid.
        if (!isStaleListResponse({ getState, preset, page, sortBy, filter })) {
          dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: [], count: 0 }));
        }

        dispatch(handleError({ error }));
      });
  };

export const debouncedBulkLabels = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listBulkLabelsByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const filterBulkLabels =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedBulkLabels({ preset, dispatch, getState });
  };

export const sortBulkLabelsWorkQueue =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedBulkLabels({ preset, dispatch, getState });
  };

export const listOrdersForProductFulfillmentByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];
    const updatedFilter = applyFulfillingPharmacyFilter(getState, filter);

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listOrdersForProductFulfillmentApi({ filter: updatedFilter, page, sortBy })
      .then(({ count, orders }) => {
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: orders, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedListOrdersForProductFulfillment = debounce(({ dispatch }) => {
  return dispatch(listOrdersForProductFulfillmentByPage({ page: 1 }));
}, 1000);

export const sortListOrdersForProductFulfillmentWorkQueue =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedListOrdersForProductFulfillment({ preset, dispatch });
  };

export const filterListOrdersForProductFulfillment =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedListOrdersForProductFulfillment({ preset, dispatch });
  };

export const listOrdersByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];
    const updatedFilter = applyFulfillingPharmacyFilter(getState, filter);

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    const { orderStatus, ...restFilter } = updatedFilter;
    return listOrderLineItemsApi({
      page,
      sortBy,
      filter: { ...restFilter, orderStatuses: orderStatus },
    })
      .then(({ count, orderLineItems }) => {
        dispatch(
          workQueueActions.loadWorkQueueToPage({ preset, page, items: orderLineItems, count })
        );
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedListOrders = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listOrdersByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const filterOrders =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedListOrders({ preset, dispatch, getState });
  };

export const sortOrders =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedListOrders({ preset, dispatch, getState });
  };

export const listOrdersForShipmentByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listOrdersForShipmentApi({
      page,
      sortBy,
      filter,
    })
      .then(({ count, orders }) => {
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: orders, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const getOrderShipmentsByPage =
  ({ page, preset: presetOverride }) =>
  (dispatch, getState) => {
    const state = getState();
    const preset = presetOverride || state.workQueue.preset;

    const presetState = preset && state.workQueue[preset];
    if (!presetState) {
      // No valid workQueue preset for shipments; skip refreshing the legacy workQueue table.
      return Promise.resolve();
    }

    const { sortBy, filter } = presetState;
    const limit = 25;
    const offset = (page - 1) * limit;

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return getOrderShipmentsApi({
      limit,
      offset,
      sortBy,
      filter,
    })
      .then(({ count, result }) => {
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: result, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

let activeUpdateController = null;

export const updateShipment =
  ({ input, page, preset, onSuccess, onError, suppressGlobalError }) =>
  async (dispatch) => {
    if (activeUpdateController) {
      activeUpdateController.abort();
    }
    activeUpdateController = new AbortController();
    const { signal } = activeUpdateController;

    try {
      const updatedShipment = await updateShipmentApi({ input, signal });
      if (signal.aborted) return undefined;
      if (updatedShipment?.shipmentId && updatedShipment?.tags) {
        dispatch(
          shipmentModuleActions.updateShipmentListItemTags({
            shipmentId: updatedShipment.shipmentId,
            tags: updatedShipment.tags,
          })
        );
      }
      if (typeof onSuccess === 'function') {
        onSuccess(updatedShipment);
      }
      if (page) {
        dispatch(getOrderShipmentsByPage({ page, preset }));
      }
      return updatedShipment;
    } catch (error) {
      if (error.name === 'AbortError' || signal.aborted) return undefined;
      // POM-5507: the ShipmentViewDialog surfaces update/create-label errors
      // inline; suppress the global blocking dialog when the caller opts in.
      if (!suppressGlobalError) {
        dispatch(handleError({ error }));
      }
      if (typeof onError === 'function') {
        onError(error);
      }
      throw error;
    } finally {
      if (activeUpdateController?.signal === signal) {
        activeUpdateController = null;
      }
    }
  };

export const deletePrxShipment =
  ({ shipmentId, cancelOrder, reason, page, preset, onSuccess, onError }) =>
  (dispatch) => {
    let cancelType;
    if (cancelOrder) {
      cancelType = 'ORDER_CANCEL';
    } else {
      cancelType = 'SHIP_LATER_REVIEW';
    }
    return deletePrxShipmentApi({ shipmentId, reason, cancelType })
      .then((result) => {
        if (typeof onSuccess === 'function') {
          onSuccess(result);
        }
        if (page) {
          dispatch(getOrderShipmentsByPage({ page, preset }));
        }
        return result;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        if (typeof onError === 'function') {
          onError(error);
        }
        throw error;
      });
  };

export const debouncedUpdateShipment = debounce(
  ({ dispatch, input, page, preset, onSuccess, onError, suppressGlobalError }) => {
    return dispatch(
      updateShipment({ input, page, preset, onSuccess, onError, suppressGlobalError })
    );
  },
  1000
);

export const debouncedGetOrderShipments = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(getOrderShipmentsByPage({ page: 1, preset })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const filterGetOrderShipments =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedGetOrderShipments({ preset, dispatch, getState });
  };

export const filterOrderShipments =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedGetOrderShipments({ preset, dispatch, getState });
  };

export const sortGetOrderShipments =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedGetOrderShipments({ preset, dispatch, getState });
  };

export const debouncedListOrdersForShipment = debounce(({ dispatch }) => {
  return dispatch(listOrdersForShipmentByPage({ page: 1 }));
}, 1000);

export const filterOrdersForShipment =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedListOrdersForShipment({ preset, dispatch });
  };

export const sortOrdersForShipment =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedListOrdersForShipment({ preset, dispatch });
  };

export const sortShipmentOrders =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedListOrdersForShipment({ preset, dispatch });
  };

export const listFailedAdjudicationsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listFailedAdjudicationsApi({
      page,
      sortBy,
      filter,
    })
      .then(({ count, results }) =>
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: results, count }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedlistAdjudication = debounce(({ dispatch }) => {
  return dispatch(listFailedAdjudicationsByPage({ page: 1 }));
}, 1000);

export const filterReviewAdjudications =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedlistAdjudication({ preset, dispatch });
  };

export const sortReviewAdjudications =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedlistAdjudication({ preset, dispatch });
  };

const hasPinnedNote = (notes) => some(notes, ['noteState', NoteState.PIN]);

export const addFailedAdjudicationNotes =
  ({ mpi, content, commentContext, contextId }) =>
  (dispatch, getState) => {
    const { currentPage, pages } = getState().workQueue.reviewAdjudication;
    const existingNotes = find(pages[currentPage], { prescriptionId: contextId })?.notes || [];

    return createLineItemNoteApi({ mpi, content, commentContext })
      .then((newNote) => {
        const updatedNotes = [newNote, ...existingNotes];

        dispatch(
          workQueueActions.loadFailedAdjudicationNotes({
            notes: updatedNotes,
            contextId,
            hasPinnedNotes: hasPinnedNote(updatedNotes),
          })
        );

        return !!newNote;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const listFailedAdjdicatioNotes =
  ({ contextId, contextType, mpi }) =>
  (dispatch) => {
    const filter = { noteContext: { noteContextIds: [contextId], type: contextType } };

    return listLineItemNotesApi({ mpi, contextType, filter })
      .then((notes) => {
        dispatch(
          workQueueActions.loadFailedAdjudicationNotes({
            notes,
            contextId,
            hasPinnedNotes: hasPinnedNote(notes),
          })
        );

        return !!notes;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const updateFailedAdjudicationNoteStatus =
  ({ contextId, noteId, action: actionState }) =>
  (dispatch, getState) => {
    const { currentPage, pages } = getState().workQueue.reviewAdjudication;
    const existingNotes = find(pages[currentPage], { prescriptionId: contextId })?.notes || [];
    const updatedNotes = map(existingNotes, (note) =>
      note.noteId === noteId ? { ...note, noteState: actionState } : note
    );

    dispatch(
      workQueueActions.loadFailedAdjudicationNotes({
        notes: updatedNotes,
        contextId,
        hasPinnedNotes: hasPinnedNote(updatedNotes),
      })
    );

    return noteActionApi({ noteId, action: actionState }).catch((error) => {
      dispatch(
        workQueueActions.loadFailedAdjudicationNotes({
          notes: updatedNotes,
          contextId,
          hasPinnedNotes: hasPinnedNote(existingNotes),
        })
      );

      dispatch(handleError({ error }));
    });
  };

export const updateRefillAdjudicationState =
  ({ rxRequestId, state }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy } = getState().workQueue[preset];

    return updateRefillAdjudicationStateApi({ rxRequestId, state })
      .then(() => {
        dispatch(sortReviewAdjudications({ sortBy }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const getProfileIncompletePatientsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    const profileIncompleteFilter = { ...filter, status: PatientStatus.PROFILE_INCOMPLETE };

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return getPatientsApi({ page, sortBy, filter: profileIncompleteFilter })
      .then(({ count, patients }) => {
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: patients, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedProfileIncompletePatients = debounce(({ dispatch }) => {
  return dispatch(getProfileIncompletePatientsByPage({ page: 1 }));
}, 1000);

export const sortProfileIncompletePatients =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));
    debouncedProfileIncompletePatients({ preset, dispatch });
  };

export const filterProfileIncompletePatients =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateFilter({ preset, filter }));
    debouncedProfileIncompletePatients({ preset, dispatch });
  };

export const getPrescriptionsNeedsReviewByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];
    const updatedFilter = applyFulfillingPharmacyFilter(getState, filter);

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listPrescriptionsForReviewApi({ page, sortBy, filter: updatedFilter })
      .then(({ count, prescriptions }) => {
        dispatch(
          workQueueActions.loadWorkQueueToPage({ preset, page, items: prescriptions, count })
        );
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedListPrescriptionsForReview = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(getPrescriptionsNeedsReviewByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const loadPrescriptionData =
  ({ lineItemId, prescriptionId, reviewType }) =>
  (dispatch) => {
    return getPrescriptionReviewDataApi({ lineItemId, prescriptionId, reviewType })
      .then(
        ({
          prescriptionData,
          existingPrescriptions,
          status,
          directionReview,
          parentLineItemQuantity,
        }) => {
          dispatch(
            workQueueActions.loadPrescriptionData({
              prescriptionId,
              prescriptionData,
              existingPrescriptions,
              status,
              directionReview,
              parentLineItemQuantity,
            })
          );

          return {
            prescriptionData,
            existingPrescriptions,
            status,
            directionReview,
            parentLineItemQuantity,
          };
        }
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const sortPrescriptionsNeedsReview =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedListPrescriptionsForReview({ preset, dispatch, getState });
  };

export const filterPrescriptionsNeedsReview =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));

    dispatch(workQueueActions.updateFilter({ preset, filter }));
    debouncedListPrescriptionsForReview({ preset, dispatch, getState });
  };

export const syncSig =
  ({
    prescriptionId,
    sig,
    sigId,
    hasOneTimeAssociation,
    confirmDirectionReassignment,
    lineItemId,
  }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { filter } = getState().workQueue[preset];

    return syncSigApi({
      prescriptionId,
      sig,
      sigId,
      hasOneTimeAssociation,
      confirmDirectionReassignment,
      lineItemId,
    })
      .then((isSynced) => {
        dispatch(filterPrescriptionsNeedsReview({ filter }));

        return isSynced;
      })
      .catch((error) => {
        const errorExtensions = first(error.response?.errors)?.extensions;

        // This one is a question, not a failure: the direction already means something else, and
        // only the reviewer can say whether to change it. A toast would leave them unable to
        // resolve the order at all, so it is handed back for the modal to ask.
        if (errorExtensions?.code === DIRECTION_ALREADY_MAPPED) {
          return {
            needsDirectionReassignmentConfirmation: true,
            currentSig: errorExtensions.currentSig,
            currentSigId: errorExtensions.currentSigId,
          };
        }

        return dispatch(handleError({ error }));
      });
  };

export const resolveDuplicatePrescription =
  ({ prescriptionId, orderId, isDuplicate, lineItemId }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { filter } = getState().workQueue[preset];

    return resolveDuplicatePrescriptionApi({ prescriptionId, orderId, isDuplicate, lineItemId })
      .then((resolveDuplicatePrescriptionReview) => {
        dispatch(filterPrescriptionsNeedsReview({ filter }));

        return resolveDuplicatePrescriptionReview;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const resolveShipLaterReview =
  ({ orderId, lineItemId, patientId, targetState }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { filter } = getState().workQueue[preset];

    return resolveShipLaterReviewApi({ orderId, lineItemId, patientId, targetState })
      .then((response) => {
        dispatch(filterPrescriptionsNeedsReview({ filter }));
        return response;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const resolveDrugProfile = (input) => (dispatch, getState) => {
  const { preset } = getState().workQueue;
  const { filter } = getState().workQueue[preset];

  return resolveDrugProfileApi(input)
    .then((response) => {
      dispatch(filterPrescriptionsNeedsReview({ filter }));
      return response;
    })
    .catch((error) => dispatch(handleError({ error })));
};

// POM-5705: associate a catalog product to a prescription in drug review (inventory-module path).
// Mirrors resolveDrugProfile — refreshes the review queue on success.
export const associateToCatalog = (input) => (dispatch, getState) => {
  const { preset } = getState().workQueue;
  const { filter } = getState().workQueue[preset];

  return associateCatalogProductApi(input)
    .then((response) => {
      dispatch(filterPrescriptionsNeedsReview({ filter }));
      return response;
    })
    .catch((error) => dispatch(handleError({ error })));
};

export const resolveMultiRxReview = (input) => (dispatch, getState) => {
  const { preset } = getState().workQueue;
  const { filter } = getState().workQueue[preset];

  return multiRxLineItemApi(input)
    .then((response) => {
      dispatch(filterPrescriptionsNeedsReview({ filter }));
      return response;
    })
    .catch((error) => {
      // POM-5805: surface the error dialog, then re-throw so the caller does NOT treat a
      // failed resolve as success (was showing a success snackbar on API failure).
      dispatch(handleError({ error }));
      throw error;
    });
};

export const resolveCentralFillStateNotLicensedReview = (input) => (dispatch, getState) => {
  const { preset } = getState().workQueue;
  const { filter } = getState().workQueue[preset];

  return resolveCentralFillReviewApi(input)
    .then((response) => {
      dispatch(filterPrescriptionsNeedsReview({ filter }));
      return response;
    })
    .catch((error) => {
      dispatch(handleError({ error }));
      throw error;
    });
};

export const overridePrescription =
  ({ input, fromWorkQueue, isPrescriptionEdit, isFillEdit }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const filter = getState().workQueue[preset]?.filter;

    const { input: inputPayload } = input;

    const overridePayload = {
      ...inputPayload,
      ...(inputPayload?.prescriptionDetails && {
        prescriptionDetails: omit(inputPayload.prescriptionDetails, 'prescriberName'),
      }),
    };

    return overridePrescriptionAPI(
      { input: overridePayload },
      fromWorkQueue,
      isPrescriptionEdit,
      isFillEdit
    )
      .then((response) => {
        if (fromWorkQueue) {
          dispatch(filterPrescriptionsNeedsReview({ filter }));
        }
        return response;
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const listEVisitRequestsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];
    const updatedFilter = applyFulfillingPharmacyFilter(getState, filter);

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listEVisitRequestsApi({ page, sortBy, filter: updatedFilter })
      .then(({ count, results }) =>
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: results, count }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedEVisitRequests = debounce(({ dispatch }) => {
  return dispatch(listEVisitRequestsByPage({ page: 1 }));
}, 1000);

export const sortInEVisitRequestsByPage =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedEVisitRequests({ preset, dispatch });
  };

export const filterEVisitRequests =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedEVisitRequests({ preset, dispatch });
  };

export const listRxIntakeRequestsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listRxIntakeRequestsApi({ page, sortBy, filter })
      .then(({ count, result }) =>
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items: result, count }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedRxIntakeRequests = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listRxIntakeRequestsByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const updateRxIntakeRequestSort =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedRxIntakeRequests({ preset, dispatch, getState });
  };

export const updateRxIntakeRequestFilter =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedRxIntakeRequests({ preset, dispatch, getState });
  };

export const listCentralFillQueueByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listCentralFillOrdersApi({ page, sortBy, filter })
      .then(({ items, totalCount }) =>
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items, count: totalCount }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

/**
 * POM-6099 — the relay sub-queue of the Central Fill Rx tab.
 *
 * `preset` is passed EXPLICITLY as 'relayQueue' rather than read from state, because the ACTIVE
 * preset is still 'compoundingQueue' while relay is on screen: relay is a mode of that tab, not a
 * tab of its own. The generic reducers key on `state[preset]`, so letting it default here would
 * write relay orders into the central fill queue pages and render one queue under the other's
 * columns, with nothing to signal it.
 */
export const listRelayQueueByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const preset = 'relayQueue';
    const { filter, sortBy } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listRelayOrdersApi({ page, filter, sortBy })
      .then(({ items, totalCount }) =>
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items, count: totalCount }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

/**
 * POM-6099. Apply the relay queue's filter and reload from page 1.
 *
 * `preset: 'relayQueue'` is written LITERALLY, never taken from `getState().workQueue.preset` the
 * way updateCentralFillQueueFilter does. Relay is a MODE INSIDE the Central Fill Rx tab, so the
 * preset there reads COMPOUNDING_QUEUE -- taking it from state would write the relay filter onto
 * the central fill queue and filter the wrong table. The slice warns about the same trap for
 * updateCurrentPage.
 *
 * PAGE 1, not the current page: filtering while on page 3 requests an offset the filtered result
 * may not reach, and the operator gets an empty page that reads as "no matches" when there are
 * matches on page 1.
 */
export const updateRelayQueueFilter =
  ({ filter }) =>
  (dispatch) => {
    dispatch(workQueueActions.updateFilter({ preset: 'relayQueue', filter }));

    return dispatch(listRelayQueueByPage({ page: 1 }));
  };

/** POM-6279. Sort the relay queue and reload from page 1. */
export const updateRelayQueueSort =
  ({ sortBy }) =>
  (dispatch) => {
    dispatch(workQueueActions.updateSortBy({ preset: 'relayQueue', sortBy }));

    return dispatch(listRelayQueueByPage({ page: 1 }));
  };

export const repostCentralFillOrder =
  ({ trackingId }) =>
  (dispatch, getState) => {
    const { currentPage } = getState().workQueue.compoundingQueue;

    return repostCentralFillOrderApi(trackingId)
      .then((response) => {
        // refresh the current page so the row's new status (QUEUED or failure) shows
        dispatch(listCentralFillQueueByPage({ page: currentPage }));
        return response; // caller decides snackbar based on response.reposted
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        throw error;
      });
  };

export const markCentralFillOrderDelivered =
  ({ trackingId, orderId, reason, deliveredDate }) =>
  (dispatch, getState) => {
    const { currentPage } = getState().workQueue.compoundingQueue;

    return updateCentralFillOrderStatusApi({
      trackingId,
      orderId,
      status: 'DELIVERED',
      reason,
      deliveredDate,
    })
      .then((response) => {
        // refresh the current page so the row leaves the compounding queue
        dispatch(listCentralFillQueueByPage({ page: currentPage }));
        return response;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        throw error;
      });
  };

export const debouncedCompoundingQueue = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listCentralFillQueueByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const updateCompoundingQueueSort =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedCompoundingQueue({ preset, dispatch, getState });
  };

export const updateCentralFillQueueFilter =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedCompoundingQueue({ preset, dispatch, getState });
  };

// ---------------------------------------------------------------------------
// Fill Later work queue (POM-6149). Same page-cache plumbing as the Central Fill queue.
// ---------------------------------------------------------------------------

export const listFillLaterByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listFillLaterItemsApi({ page, sortBy, filter })
      .then(({ items, totalCount }) =>
        dispatch(workQueueActions.loadWorkQueueToPage({ preset, page, items, count: totalCount }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedFillLater = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listFillLaterByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const updateFillLaterSort =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedFillLater({ preset, dispatch, getState });
  };

export const updateFillLaterFilter =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedFillLater({ preset, dispatch, getState });
  };

// After a row action resolves, refresh the page the user is looking at NOW - not the one they
// were on when the request started. Reading state at resolution time (and checking the tab is
// still Fill Later) means a page or tab change made while the request was in flight can neither
// snap the view back nor write Fill Later rows into another queue's page cache.
const refreshFillLaterPageIfActive = (dispatch, getState) => {
  const { preset, fillLater } = getState().workQueue;
  if (preset !== WorkQueues.FILL_LATER) return;
  dispatch(listFillLaterByPage({ page: fillLater.currentPage }));
};

// Change the date (POM-6156). Resolves with the updated row, or undefined after a handled error
// so the dialog can stay open for another try.
export const updateFillLaterAddress =
  ({ fillLaterItemId, addressDirectoryId, isManuallyVerified }) =>
  (dispatch, getState) =>
    updateFillLaterAddressApi({ fillLaterItemId, addressDirectoryId, isManuallyVerified })
      .then((item) => {
        refreshFillLaterPageIfActive(dispatch, getState);
        return item;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return undefined;
      });

export const rescheduleFillLaterItem =
  ({ fillLaterItemId, scheduledFor }) =>
  (dispatch, getState) =>
    rescheduleFillLaterItemApi({ fillLaterItemId, scheduledFor })
      .then((item) => {
        refreshFillLaterPageIfActive(dispatch, getState);
        return item;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return undefined;
      });

// Create now (POM-6161, backend POM-6150). Every outcome - CREATED, AWAITING_SHIPMENT,
// ALREADY_HANDLED, FAILED - is a normal result the dialog turns into a toast, so the page is
// refreshed for all of them. Only a transport / GraphQL error goes to handleError and resolves
// undefined, which keeps the dialog open.
export const createFillLaterItemNow =
  ({ fillLaterItemId }) =>
  (dispatch, getState) =>
    createFillLaterItemNowApi({ fillLaterItemId })
      .then((result) => {
        refreshFillLaterPageIfActive(dispatch, getState);
        return result;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return undefined;
      });

// Merge with parent order (POM-6182). The backend re-checks the row status and the parent's
// eligibility under row locks, so a stale row is refused there with a typed error; that error is
// reported through handleError and resolves undefined so the dialog stays open, and the page is
// refreshed only after a successful merge.
export const mergeFillLaterItem =
  ({ fillLaterItemId }) =>
  (dispatch, getState) =>
    mergeFillLaterItemApi({ fillLaterItemId })
      .then((item) => {
        refreshFillLaterPageIfActive(dispatch, getState);
        return item;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return undefined;
      });

// ---------------------------------------------------------------------------
// Non-Routable Orders holding queue (POM-6223).
//
// EVERY ROW IS A PRESCRIPTION THAT IS NOT BEING FILLED. Parking writes no rx_request, rx_order,
// rx_request_line_item or prescription row and notifies no partner, so this queue is the only
// place the request is visible and the only route by which a human acts on it.
//
// Deliberately NOT routed through `handleError`, unlike every other queue here: these resolvers
// raise TYPED codes whose meanings are not interchangeable — three of them are raised AFTER the
// prescription has already been released — and the generic error modal would flatten all of them
// into one message the operator could safely click through twice. The error is handed back to the
// caller, which keys on the code. See modules/WorkQueue/utils/nonRoutableRequestErrors.js.
// ---------------------------------------------------------------------------

export const listNonRoutableRequestsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return (
      listNonRoutableRequestsApi({ page, sortBy, filter })
        .then(({ items, totalCount }) => {
          // POM-6061's guard, ported. A response asked for under a sort, filter or page the
          // operator has since left must not repaint the screen they are looking at now.
          if (isStaleListResponse({ getState, preset, page, sortBy, filter })) return;

          dispatch(
            workQueueActions.loadWorkQueueToPage({ preset, page, items, count: totalCount })
          );
        })
        // A FAILED READ MUST NOT LOOK LIKE AN EMPTY QUEUE (design §9): the page is deliberately left
        // unloaded, so the table keeps its loading state rather than showing "Nothing found", and
        // `handleError` raises the standard modal over it. That modal is the ONLY signal the
        // operator gets that the read failed — do not simplify this catch into a silent no-op.
        .catch((error) => dispatch(handleError({ error })))
    );
  };

export const debouncedNonRoutableRequests = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listNonRoutableRequestsByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const updateNonRoutableRequestsSort =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedNonRoutableRequests({ preset, dispatch, getState });
  };

export const updateNonRoutableRequestsFilter =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedNonRoutableRequests({ preset, dispatch, getState });
  };

// Refresh the page the operator is looking at NOW, and only while this tab is still the one on
// screen — a tab change made while a mutation was in flight must not write these rows into
// another queue's page cache.
export const refreshNonRoutableRequestsIfActive = () => (dispatch, getState) => {
  const { preset, nonRoutableRequests } = getState().workQueue;
  if (preset !== WorkQueues.NON_ROUTABLE_REQUESTS) return undefined;
  return dispatch(listNonRoutableRequestsByPage({ page: nonRoutableRequests.currentPage }));
};

// A single parked request at ANY status. Resolves `null` when no request carries that id — a
// primary-key miss is a legitimate answer, which is why the backend field is nullable. Used to
// tell the operator what a row's status actually IS after a NOT_PENDING refusal.
export const fetchNonRoutableRequest =
  ({ nonRoutableRequestId }) =>
  () =>
    getNonRoutableRequestApi({ nonRoutableRequestId }).catch(() => undefined);

// IRREVERSIBLE: releases the parked request into the normal POM flow, creating an order that WILL
// BE DISPENSED. Resolves `{ detail }` on success or `{ error }` with the raw error so the caller
// can key on the typed code; it deliberately does not throw and does not show an error itself.
//
// IT ALSO DOES NOT REFRESH THE QUEUE, and that is deliberate rather than forgotten. Both dialogs
// are rendered PER ROW, so refreshing here dropped the resolved row and unmounted the dialog that
// was still showing the operator what had happened to it. The refresh moved to the dialog's own
// dismissal (`useNonRoutableResolution`), which is the point at which the message has been read.
// Any future caller of these two thunks has to refresh for itself.
export const bringBackNonRoutableRequestToPom =
  ({ nonRoutableRequestId }) =>
  () =>
    bringBackNonRoutableRequestToPomApi({ nonRoutableRequestId })
      .then((detail) => ({ detail }))
      .catch((error) => ({ error }));

export const moveRelayFaxOrderToPom =
  ({ nonRoutableRequestId }) =>
  () =>
    moveRelayFaxOrderToPomApi({ nonRoutableRequestId })
      .then((detail) => ({ detail }))
      .catch((error) => ({ error }));

// IRREVERSIBLE in the opposite direction: the prescription is never filled; the partner is sent
// the ORDER_CANCELED webhook carrying `cancelReason`.
export const cancelNonRoutableRequest =
  ({ nonRoutableRequestId, cancelReason }) =>
  () =>
    cancelNonRoutableRequestApi({ nonRoutableRequestId, cancelReason })
      .then((detail) => ({ detail }))
      .catch((error) => ({ error }));

// RE-RUNS ROUTING FROM THE FIRST STEP. Unlike the other two this is NOT terminal: the request may
// come back still parked, with its attempt count incremented, and that is a successful call rather
// than an error. Same contract as its siblings — resolves `{ detail }` or `{ error }`, never throws,
// and does NOT refresh the queue, because this dialog is rendered per row and a refresh would drop
// the row out from under the message the operator is reading.
export const retryNonRoutableRequestRouting =
  ({ nonRoutableRequestId }) =>
  () =>
    retryNonRoutableRequestRoutingApi({ nonRoutableRequestId })
      .then((detail) => ({ detail }))
      .catch((error) => ({ error }));

// ---- POM-6279: parked RELAY FAX ------------------------------------------------------------
//
// SAME CONTRACT AS THE THREE ABOVE: resolve, never throw, never route through `handleError`, never
// refresh the queue (the dialog refreshes on dismissal). The fax mutations return a
// RelayFaxActionResult rather than a bare detail, so they resolve `{ detail, result }` — `detail` is
// the row as the database now has it (`result.request`), which is what useNonRoutableResolution
// keys on, and `result` carries `outcome` and `faxMayHaveBeenSent` for the dialog's copy.
const toRelayFaxActionOutcome = (promise) =>
  promise.then((result) => ({ detail: result.request, result })).catch((error) => ({ error }));

// `confirmResend` is passed through only as given: true after the operator confirmed by phone that
// the pharmacy did NOT receive the fax, absent on the first click.
export const resendRelayFax =
  ({ nonRoutableRequestId, confirmResend }) =>
  () =>
    toRelayFaxActionOutcome(resendRelayFaxApi({ nonRoutableRequestId, confirmResend }));

export const reconcileRelayFax =
  ({ nonRoutableRequestId }) =>
  () =>
    toRelayFaxActionOutcome(reconcileRelayFaxApi({ nonRoutableRequestId }));

export const cancelRelayFax =
  ({ nonRoutableRequestId }) =>
  () =>
    toRelayFaxActionOutcome(cancelRelayFaxApi({ nonRoutableRequestId }));

// One parked relay fax in full, for the fax detail view. Resolves `{ detail }` — `null` when the id
// is not a parked fax, which is an answer — or `{ error }` so the view can describe the typed code
// in place rather than raising the global modal over a dialog.
export const fetchNonRoutableRelayFaxDetail =
  ({ nonRoutableRequestId }) =>
  () =>
    getNonRoutableRelayFaxDetailApi({ nonRoutableRequestId })
      .then((detail) => ({ detail }))
      .catch((error) => ({ error }));
export const cancelFillLaterItem =
  ({ fillLaterItemId, cancelReason, cancelNote }) =>
  (dispatch, getState) =>
    cancelFillLaterItemApi({ fillLaterItemId, cancelReason, cancelNote })
      .then((items) => {
        refreshFillLaterPageIfActive(dispatch, getState);
        return items;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        return undefined;
      });

export const importEScriptRequests = (input) => (dispatch, getState) => {
  const { preset } = getState().workQueue;
  const { filter } = getState().workQueue[preset];

  return importEScriptRequestsApi(input)
    .then((response) => {
      dispatch(updateRxIntakeRequestFilter({ filter }));
      return response;
    })
    .catch((error) => dispatch(handleError({ error })));
};

export const closeEScriptRequests = (eScriptIds) => (dispatch, getState) => {
  const { preset } = getState().workQueue;
  const { filter } = getState().workQueue[preset];

  return closeEScriptRequestsApi(eScriptIds)
    .then((response) => {
      dispatch(updateRxIntakeRequestFilter({ filter }));
      return response;
    })
    .catch((error) => dispatch(handleError({ error })));
};

export const listProcessedExternalOrdersByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    if (preset !== 'processedExternalOrders') return undefined;

    const { filter, sortBy } = getState().workQueue.processedExternalOrders;

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return listProcessedExternalOrdersApi({ page, sortBy, filter })
      .then(({ count, orders }) =>
        dispatch(workQueueActions.loadWorkQueueToPage({ count, items: orders, page, preset }))
      )
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedProcessedExternalOrders = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listProcessedExternalOrdersByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const updateProcessedExternalOrdersFilter =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedProcessedExternalOrders({ preset, dispatch, getState });
  };

export const updateProcessedExternalOrdersSortBy =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.updateSortBy({ preset, sortBy }));

    debouncedProcessedExternalOrders({ preset, dispatch, getState });
  };

export const multipleTransferOrderToFacility =
  ({ orderIds, lineItemIds, fulfillingPharmacyId, toFacilityId }) =>
  (dispatch) =>
    transferOrderToFacilityApi({
      orderIds,
      lineItemIds,
      fulfillingPharmacyId: fulfillingPharmacyId || toFacilityId,
    })
      // POM-5945: resolves with the response envelope { results, outcomes, totals }, passed through
      // whole so the caller can attribute reasons back to the selected line items. A rejection
      // resolves to undefined via handleError, which the caller reads as ERROR.
      .then((transferResponse) => transferResponse)
      .catch((error) => dispatch(handleError({ error })));

export const listUnReconciledOrdersByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { filter } = getState().workQueue[preset];

    dispatch(workQueueActions.updateCurrentPage({ preset, page }));

    return getUnReconciledShipmentsListApi({ page, limit: 25, filter })
      .then((response) => {
        const count = response?.count ?? 0;
        const result = response?.result ?? [];
        dispatch(
          workQueueActions.loadWorkQueueToPage({
            count,
            items: result,
            page,
            preset,
          })
        );
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const debouncedUnReconciledOrders = debounce(({ preset, dispatch, getState }) => {
  const currentPreset = getState().workQueue.preset;
  if (currentPreset !== preset) {
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }));
    return undefined;
  }

  return dispatch(listUnReconciledOrdersByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: false }))
  );
}, 1000);

export const updateUnReconciledOrdersFilter =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.setWorkQueueLoading({ preset, loading: true }));
    dispatch(workQueueActions.updateFilter({ preset, filter }));

    debouncedUnReconciledOrders({ preset, dispatch, getState });
  };

export const deleteUnreconcilableOrders =
  ({ spOrderIds }) =>
  (dispatch) => {
    dispatch(workQueueActions.setOrderDeleteLoading(true));
    return deleteIrreconciledOrderApi({ spOrderIds })
      .then((response) => {
        dispatch(workQueueActions.setOrderDeleteLoading(false));
        return response;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      });
  };

export const getOrdersSuggestionsForShipstation =
  ({ input, filter, page = 1, limit = 25 }) =>
  (dispatch) => {
    dispatch(workQueueActions.setSuggestionsLoading(true));
    return getOrderSuggestionsForShipstationOrdersApi({ input, filter, page, limit })
      .then((response) => {
        const { count, result } = response?.getOrderSuggestions || response;
        dispatch(
          workQueueActions.loadOrdersSuggestionsToPage({
            page,
            items: result,
            count,
          })
        );
        return response;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
      })
      .finally(() => {
        dispatch(workQueueActions.setSuggestionsLoading(false));
      });
  };

export const associateOrderShipment =
  ({ input }) =>
  (dispatch) => {
    dispatch(workQueueActions.setOrderLinkLoading(true));
    return associateOrderShipmentApi({ input })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        dispatch(workQueueActions.setOrderLinkLoading(false));
      });
  };

export const listBulkLabelPrintHistoryByPage =
  ({ page }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    const { sortBy, filter } = getState().workQueue[preset].bulkLabelHistory;

    dispatch(workQueueActions.updateBulkHistoryPage({ preset, page }));

    return listBulkLabelPrintHistoryApi({ filter, page, sortBy })
      .then(({ count, results }) => {
        dispatch(
          workQueueActions.loadBulkHistoryToPage({
            preset,
            page,
            items: results,
            count,
          })
        );
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedBulkLabelHistory = debounce(({ preset, dispatch }) => {
  return dispatch(listBulkLabelPrintHistoryByPage({ page: 1 })).finally(() =>
    dispatch(workQueueActions.setBulkHistoryViewLoading({ preset, loading: false }))
  );
}, 1000);

export const filterBulkLabelHistory =
  ({ filter }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;

    dispatch(workQueueActions.setBulkHistoryViewLoading({ preset, loading: true }));
    dispatch(workQueueActions.setUpdateFilterOnHistory({ preset, filter }));

    debouncedBulkLabelHistory({ preset, dispatch });
  };

export const sortBulkLabelHistoryQueue =
  ({ sortBy }) =>
  (dispatch, getState) => {
    const { preset } = getState().workQueue;
    dispatch(workQueueActions.updateBulkLabelHistorySortBy({ preset, sortBy }));

    debouncedBulkLabelHistory({ preset, dispatch });
  };

export const fetchLockedInventoryItems = (fulfillingPharmacyIds) => (dispatch) => {
  if (!EnableDispenseFlow) {
    return dispatch(workQueueActions.setLockedInventoryItems([]));
  }

  // Opens the buffer window: lock changes pushed while this read is in flight are replayed on
  // top of the response, which was computed before they happened (POM-6033).
  dispatch(workQueueActions.physicalCountSyncStarted());

  return listLockedInventoryItemsApi({ fulfillingPharmacyIds })
    .then((result) => {
      dispatch(workQueueActions.setLockedInventoryItems(result));
    })
    .catch((error) => {
      // Close the window too, or every later delta is buffered forever against a read that
      // will never land.
      dispatch(workQueueActions.physicalCountSyncFailed());
      return dispatch(handleError({ error }));
    });
};

/**
 * Applies a physical-count lock change pushed over the socket (POM-6033).
 *
 * Facilities are filtered here rather than trusted from the wire: the socket server scopes rooms
 * by facility, but nothing stops a client from subscribing to one its user cannot see, and the
 * banner must never name a facility outside the user's own list.
 */
export const applyPhysicalCountLockChange =
  ({ changeType, items }) =>
  (dispatch, getState) => {
    if (!EnableDispenseFlow || !items?.length) return;

    const { auth } = getState();
    const allowedFacilityIds = new Set(map(auth?.fulfillingPharmacies, 'fulfillingPharmacyId'));

    const relevantItems = items.filter(({ fulfillingPharmacyId }) =>
      allowedFacilityIds.has(fulfillingPharmacyId)
    );

    if (!relevantItems.length) return;

    dispatch(
      workQueueActions.applyPhysicalCountLockChange({
        changeType,
        // The socket bypasses api/, so this is its casing boundary. Without it the banner would
        // show a pushed drug one way and the same drug from a read another.
        items: relevantItems.map(withTitleCasedDrugFields),
      })
    );
  };

export const cancelBulkLineItems =
  ({ lineItemIds, cancelReason, note }) =>
  async (dispatch) => {
    try {
      const cancelNote = {
        content: {
          contentType: ContentType.TEXT,
          message: note,
        },
      };

      const result = await cancelBulkLineItemsApi({
        lineItemIds,
        cancelReason,
        cancelNote,
      });

      return result;
    } catch (error) {
      dispatch(handleError({ error }));
      throw error;
    }
  };

const dispatchPatientShipByDateUpdate = (
  dispatch,
  { mpi, orderId, lineItemId, shipByDate, tags }
) => {
  if (mpi && orderId) {
    dispatch(
      patientActions.updateLineItemShipByDate({ mpi, orderId, lineItemId, shipByDate, tags })
    );
  }
};

export const setLineItemShipByDate =
  ({ lineItemId, shipByDate, reason, mpi, orderId }) =>
  async (dispatch) => {
    const result = await setLineItemShipByDateApi({ lineItemId, shipByDate, reason });
    dispatch(
      workQueueActions.updateLineItemShipByDate({
        lineItemId: result.requestLineItemId,
        shipByDate: result.shipByDate,
        tags: result.tags,
      })
    );
    dispatchPatientShipByDateUpdate(dispatch, {
      mpi,
      orderId,
      lineItemId,
      shipByDate: result.shipByDate,
      tags: result.tags,
    });
    return result;
  };

export const clearShipByDateOverride =
  ({ lineItemId, mpi, orderId }) =>
  async (dispatch) => {
    const result = await clearShipByDateOverrideApi({ lineItemId });
    dispatch(
      workQueueActions.updateLineItemShipByDate({
        lineItemId: result.requestLineItemId,
        shipByDate: result.shipByDate,
        tags: result.tags,
      })
    );
    dispatchPatientShipByDateUpdate(dispatch, {
      mpi,
      orderId,
      lineItemId,
      shipByDate: result.shipByDate,
      tags: result.tags,
    });
    return result;
  };

export const applyUrgentTag =
  ({ lineItemId, shipByDate, reason, mpi, orderId }) =>
  async (dispatch) => {
    const result = await applyUrgentTagApi({ lineItemId, shipByDate, reason });
    dispatch(
      workQueueActions.updateLineItemShipByDate({
        lineItemId: result.requestLineItemId,
        shipByDate: result.shipByDate,
        tags: result.tags,
      })
    );
    dispatchPatientShipByDateUpdate(dispatch, {
      mpi,
      orderId,
      lineItemId,
      shipByDate: result.shipByDate,
      tags: result.tags,
    });
    return result;
  };

export const removeUrgentTag =
  ({ lineItemId, shipByDate, reason, mpi, orderId }) =>
  async (dispatch) => {
    const result = await removeUrgentTagApi({ lineItemId, shipByDate, reason });
    dispatch(
      workQueueActions.updateLineItemShipByDate({
        lineItemId: result.requestLineItemId,
        shipByDate: result.shipByDate,
        tags: result.tags,
      })
    );
    dispatchPatientShipByDateUpdate(dispatch, {
      mpi,
      orderId,
      lineItemId,
      shipByDate: result.shipByDate,
      tags: result.tags,
    });
    return result;
  };
